import { Formik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Drawer, Field, Form, Radio } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useFeatures } from '~/hooks';
import {
  calculateExpenseMarkup,
  calculateExpenseRevenue,
  calculateMarkupRatio,
  dateFormats,
  emptyStringToNull,
  getInitialMarkupRatioValue,
  mergeValues,
} from '~/utils';

const Checkboxes = styled.div`
  display: flex;
  flex-wrap: wrap;

  > * {
    margin-right: 1.35rem;
  }
`;

export default function ProjectExpenseForm({ projectId, projectExpenseId, onSaved, onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const features = useFeatures();

  const [project, setProject] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).projects(projectId).get();
        setProject(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId]);

  const [projectExpense, setProjectExpense] = useState(null);
  useEffect(() => {
    (async () => {
      if (!projectExpenseId) {
        setProjectExpense({});
        return;
      }

      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .projectExpenses(projectExpenseId)
          .get();
        setProjectExpense(data);
      } catch {
        // Do nothing
      }
    })();
  }, [api, workspace.id, projectId, projectExpenseId]);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  if (!project || !projectExpense) return null;

  const initialValues = mergeValues(
    {
      amount: '',
      date: moment().format(dateFormats.isoDate),
      expenseCategory: null,
      isBillable: false,
      notes: '',
      vendor: '',
      enableMarkup: false,
      markupMethod: 'percentage',
      markupAmount: '',
      markupRatio: '',
    },
    {
      ...projectExpense,
      markupRatio: getInitialMarkupRatioValue({
        markupRatio: projectExpense.markupRatio,
        defaultMarkupPercentage: workspace.defaultMarkupPercentage,
      }),
      enableMarkup: !!projectExpense.markupMethod,
    },
  );

  const handleSubmit = async (values, formik) => {
    formik.setSubmitting(true);

    if (!values.isBillable || !values.enableMarkup) {
      values.markupMethod = null;
      values.markupRatio = null;
      values.markupAmount = null;
    }
    switch (values.markupMethod) {
      case 'percentage':
        values.markupAmount = null;
        break;
      case 'amount':
        values.markupRatio = null;
        break;
    }

    values = emptyStringToNull({
      ..._.omit(values, ['expenseCategory', 'enableMarkup', 'useMonthlyBudget']),
      expenseCategoryId: values.expenseCategory.id,
      markupRatio: calculateMarkupRatio({ enableMarkup: values.enableMarkup, markupRatio: values.markupRatio }),
    });

    try {
      if (projectExpenseId) {
        await api.www
          .workspaces(workspace.id)
          .projectSettings(projectId)
          .projectExpenses(projectExpenseId)
          .patch(values);
      } else {
        await api.www.workspaces(workspace.id).projectSettings(projectId).projectExpenses().post(values);
      }

      toast.success('Project Expense saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
    }

    formik.setSubmitting(false);
  };

  return (
    <Drawer
      title={projectExpenseId ? 'Edit Project Expense' : 'Add Project Expense'}
      isOpen
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              amount: Yup.number().label('Amount').min(-99999999999).max(99999999999).nullable().required(),
              date: Yup.date().label('Date').nullable().required(),
              expenseCategory: Yup.object().label('Expense Category').nullable().required(),
              notes: Yup.string().label('Notes').max(1024),
              vendor: Yup.string().label('Vendor').max(255),
              enableMarkup: Yup.bool(),
              markupMethod: Yup.string().label('Type'),
              markupAmount: Yup.number().label('Markup Amount').min(0).max(99999999999).nullable(),
              markupRatio: Yup.number().label('Markup Percentage').min(0).max(100).nullable(),
            })}>
            {(formik) => {
              let markup = calculateExpenseMarkup({ ...formik.values, amount: formik.values.amount });
              const totalToBill = calculateExpenseRevenue({ markup: markup, amount: formik.values.amount });

              const handleExpensesAmountChange = (value) => {
                // Deactivate "enableMarkup" if amount is 0 or null
                if (!value) formik.setFieldValue('enableMarkup', false);
                formik.setFieldValue('amount', value);
              };

              const handleIsBillableChange = ({ target: { value } }) => {
                // Deactivate "enableMarkup" if expense is non billable
                const newValue = !(value === 'true');
                if (!newValue) formik.setFieldValue('enableMarkup', false);
                formik.setFieldValue('isBillable', newValue);
              };

              return (
                <Form>
                  <Form.Section title="Details">
                    <Form.Control>
                      <Field.DayPicker name="date" placeholder="Date" clearable={false} />
                    </Form.Control>

                    <Form.Control>
                      <Field.ExpenseCategorySelect name="expenseCategory" placeholder="Expense Category" allowNew />
                    </Form.Control>

                    <Form.Control>
                      <Field.Text name="vendor" placeholder="Vendor" maxLength={255} />
                    </Form.Control>

                    <Form.Control>
                      <Field.Text name="notes" placeholder="Notes" maxLength={1024} />
                    </Form.Control>
                  </Form.Section>

                  <Form.Section title="Billing">
                    <Form.Control>
                      <Checkboxes>
                        <Field.Checkbox
                          onChange={handleIsBillableChange}
                          name="isBillable"
                          label="Bill to client"
                          disabled={!project.isBillable}
                        />

                        {(features.expenseMarkup || projectExpense.enableMarkup) && (
                          <Field.Checkbox
                            name="enableMarkup"
                            label="Markup Expense"
                            disabled={!formik.values.isBillable || !(formik.values.amount > 0)}
                          />
                        )}
                      </Checkboxes>
                    </Form.Control>

                    <Form.Control>
                      <Field.Currency
                        onChange={handleExpensesAmountChange}
                        name="amount"
                        placeholder="Amount"
                        currency={project.currency}
                      />
                    </Form.Control>
                  </Form.Section>

                  {formik.values.enableMarkup && formik.values.isBillable && (
                    <Form.Section title="Expense Markup">
                      <Form.Control>
                        <Field.RadioGroup name="markupMethod">
                          <Radio key="percentage" value="percentage" label="Markup by Percentage" />
                          <Radio key="amount" value="amount" label="Markup by Fixed Amount" />
                        </Field.RadioGroup>
                      </Form.Control>
                      {formik.values.markupMethod && (
                        <Form.Control>
                          {formik.values.markupMethod === 'amount' && (
                            <Field.Currency
                              currency={project.currency}
                              name="markupAmount"
                              placeholder="Markup Amount"
                            />
                          )}
                          {formik.values.markupMethod === 'percentage' && (
                            <Field.Number
                              suffix="%"
                              name="markupRatio"
                              placeholder="Markup Percentage"
                              min={0}
                              max={100}
                              precision={2}
                            />
                          )}
                        </Form.Control>
                      )}
                      <Form.Control>
                        <Field.Currency
                          name="total"
                          value={totalToBill}
                          materialPlaceholder="Total to Bill"
                          disabled
                          materialAlwaysVisible
                          currency={project.currency}
                        />
                      </Form.Control>
                    </Form.Section>
                  )}

                  <Drawer.Actions>
                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                      <Button isLoading={formik.isSubmitting} type="submit" onClick={formik.submitForm}>
                        Save & Close
                      </Button>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}
